/**
 * StyleCockpitChrome — the shared compact header every Style File screen
 * (Overview + the 7 process screens) renders around its own content: back
 * arrow to Overview, style ref + status pill, name/buyer. Product feedback:
 * the old single-page-with-10-tabs Style File was "very complicated" for
 * customers — split into a lean Overview "file" plus one focused screen per
 * process (`docs/product/garment-os-screen-build-plan.md` §A). Follow-up
 * feedback: no tab bar between the process screens either — they're reached
 * by LINKS from Overview's right rail, not lateral nav — and no separate
 * stage-stepper bar; the header's status pill is the only stage indicator,
 * so a process screen is just this compact header + the section, full
 * width, no rail of its own.
 *
 * Deliberately a shared COMPONENT, not a shared router layout route: a bare
 * `$styleId.tsx` layout file would auto-nest every existing `$styleId.*`
 * route under it (this app's flat-file router nests purely by path), which
 * would wrongly wrap the already-self-contained full-bleed `visual` and
 * `design` screens in this chrome too. Each of the 8 leaf pages fetches its
 * own `styles.get` (TanStack Query dedupes the cache hit) and passes the
 * result in here — matching this codebase's existing per-page fetch idiom
 * (`visual-style-file-page.tsx`, `tech-pack-page.tsx`), not a new pattern.
 *
 * @see prototype/garment/style-file.html — the section-anchor visual origin
 */
import type { ReactNode } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import { BackButton } from "../../workspace/index.js";
import type { AppRouter } from "@flint/api/router";
import { StatusPill } from "@flint/ui";
import { styleStatusLabel, styleStatusTone, type StyleStatus } from "../lib/status.js";

/**
 * `@flint/api-contract`'s own `AppRouter` mirror is still a placeholder
 * (every procedure typed `unknown` — see that file's own header comment),
 * so this derives the real shape the same way `lib/trpc.ts` types the
 * client itself: a type-only import straight from `apps/api`.
 */
export type StyleCockpitStyle = inferRouterOutputs<AppRouter>["styles"]["get"];

export function StyleCockpitChrome({
  style,
  children,
}: {
  /** Kept in the props contract (callers pass it) though the chrome no longer
   *  needs it now that the back arrow is history-aware, not a per-style link. */
  styleId?: string;
  style: StyleCockpitStyle;
  children: ReactNode;
}) {
  const status = style.status as StyleStatus;
  return (
    <div className="flex min-h-full flex-col bg-bg">
      <header className="sticky top-0 z-20 flex h-14 items-center gap-3.5 border-b border-line bg-paper px-6">
        {/* History-aware back (previous page), falling back to the Style files
            list when opened via a deep link — replaces a hardcoded self-link to
            the overview that never went "back". */}
        <BackButton fallbackTo="/merchandising/styles" className="rounded-sm" />
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-base font-bold leading-tight tracking-tight text-ink">
            <span className="font-mono">{style.styleRef}</span>
            <StatusPill tone={styleStatusTone(status)} size="sm" dot>
              {styleStatusLabel(status)}
            </StatusPill>
          </h1>
          <p className="mt-0.5 truncate text-xs text-muted">
            {style.name} · {style.buyerName}
          </p>
        </div>
      </header>

      <div className="mx-auto w-full max-w-[1280px] px-6 pb-16 pt-4">{children}</div>
    </div>
  );
}

/** One process screen's content card — the title + meta line, with an
 *  optional right-aligned slot on the title row (status pills, dates). */
export function SectionCard({
  title,
  meta,
  id,
  action,
  children,
}: {
  title: string;
  meta: string;
  id: string;
  action?: ReactNode | undefined;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-28 rounded-md border border-line bg-paper p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h2 className="text-base font-semibold leading-tight text-ink">{title}</h2>
          {meta ? <p className="mt-0.5 text-xs text-muted">{meta}</p> : null}
        </div>
        {action ? <div className="flex shrink-0 flex-col items-end gap-1.5">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}
